import * as React from "react";
import { LocalizedText } from "bernie-l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { UitkDialogContent, UitkFullscreenDialog } from "uitk-react-dialog";
import { UitkMap } from "uitk-react-map";
import { UitkToolbar } from "uitk-react-toolbar";
import { inject } from "mobx-react";

import { FullScreenMapProps, MarkerCardContent, MapMarker } from "./typings";
import { MapMarkers } from "./markers/MapMarkers";
import { MapMarkerCard } from "./cards/MapMarkerCard";
import { MobileMenu, DesktopMenu } from "./menus/Menus";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent as SendTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";

const MAX_NUMBER_OF_LANDMARKS = 10;

const FullScreenMap = inject("analytics")((props: FullScreenMapProps) => {
  const { destination, pois, hotels, activities, analytics, showMap, toggleMapDialog, context, maxWidthDialog } = props;
  const [showMobileNav, setShowMobileNav] = React.useState(false);
  const [selectMarker, setSelectMarker] = React.useState("");
  const [showCard, setShowCard] = React.useState(false);

  const initialCardContent = {
    id: "0",
    type: "",
    price: "",
    photoUrl: "",
    link: "",
    title: "",
    description: "",
    buttonLabel: "",
    buttonIcon: "",
    rfrr: "",
  };
  const [cardContent, setCardContent] = React.useState(initialCardContent);
  const { formatText } = useLocalization();

  const toggleMobileNav = () => {
    setShowMobileNav(!showMobileNav);
  };

  const hideCard = () => {
    setShowCard(false);
  };

  const displayCard = (newCardContent: MarkerCardContent) => {
    setSelectMarker("");
    setShowCard(true);
    setCardContent({ ...cardContent, ...newCardContent });

    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: newCardContent.rfrr,
      action: Action.CLICK,
    };
    if (analytics) {
      SendTrackEvent(flexTrackingInfo, analytics);
    }
  };

  const landmarks =
    pois &&
    pois.slice(0, MAX_NUMBER_OF_LANDMARKS).map((poi: MapMarker) => {
      const updateCard = () => {
        toggleMobileNav();
        setSelectMarker(poi.cardContent.id);
        setShowCard(true);
        setCardContent({ ...cardContent, ...poi.cardContent });

        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: `${poi.cardContent.rfrr}.Menu`,
          action: Action.CLICK,
        };
        if (analytics) {
          SendTrackEvent(flexTrackingInfo, analytics);
        }
      };

      return {
        id: poi.cardContent.id,
        title: poi.cardContent.title,
        description: poi.cardContent.description,
        onClick: updateCard,
      };
    });

  const dialogProps = {
    dialogShow: showMap,
    strictFullscreen: true,
    className: "uitk-dialog-layer",
  };

  const toolbarAttributes = {
    iconLabel: formatText("destinationDiscoveryMap.button.close"),
    header: `${destination.name}, ${destination.country}`,
  };

  const mapProps = {
    center: { lat: destination.latitude, lng: destination.longitude },
    locale: context.locale,
    countryCode: context.site.country,
    zoom: 14,
    clickablePOIs: false,
  };

  const mapMarkersProps = {
    hotels,
    pois,
    activities,
    onSelect: displayCard,
    selectMarker,
  };

  const smallView = (
    <div>
      <UitkToolbar {...toolbarAttributes} action={toggleMapDialog("close")} />

      {landmarks && (
        <div id="dx-mobile-card" className={`mobile-nav-card ${showMobileNav && "mobile-nav-card--reveal"}`}>
          <MobileMenu
            landmarks={landmarks}
            toggle={toggleMobileNav}
            isOpen={showMobileNav}
            title={formatText("destinationDiscoveryMap.title.mobile", destination.name)}
          />
        </div>
      )}

      <button className="is-visually-hidden" onClick={toggleMapDialog("close")}>
        <LocalizedText message="destinationDiscoveryMap.button.close" />
      </button>

      <UitkMap data-testid="destination-discovery-uitk-map" {...mapProps}>
        <MapMarkers {...mapMarkersProps} isMobile={true} />
      </UitkMap>
    </div>
  );

  const largeView = (
    <div>
      <UitkToolbar {...toolbarAttributes} action={toggleMapDialog("close")} />

      {landmarks && (
        <DesktopMenu
          landmarks={landmarks}
          title={formatText("destinationDiscoveryMap.title.desktop", destination.name)}
        />
      )}

      <button className="is-visually-hidden" onClick={toggleMapDialog("close")}>
        <LocalizedText message="destinationDiscoveryMap.button.close" />
      </button>

      <UitkMap data-testid="destination-discovery-uitk-map" {...mapProps}>
        <MapMarkers data-testid="check" {...mapMarkersProps} isMobile={false} />
      </UitkMap>
    </div>
  );

  React.useEffect(() => {
    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: "TG.LP.Dest.Map.close",
      action: Action.CLICK,
    };

    return () => SendTrackEvent(flexTrackingInfo, analytics);
  }, []);

  return (
    <UitkFullscreenDialog {...dialogProps}>
      <UitkDialogContent zeroPadding={true} className={`uitk-cell${maxWidthDialog ? " maxWidthDialog" : ""}`}>
        <Viewport>
          <ViewSmall>{smallView}</ViewSmall>
          <ViewLarge>{largeView}</ViewLarge>
        </Viewport>

        {cardContent && <MapMarkerCard content={cardContent} showCard={showCard} onClose={hideCard} />}
      </UitkDialogContent>
    </UitkFullscreenDialog>
  );
});

export default FullScreenMap;
